import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Save, RefreshCw, Cloud, MessageSquare, Mail, Check, X, Zap, Trash2, Clock,
} from "lucide-react";
import { useDebouncedAction } from "@/hooks/useDebouncedAction";
import SyncSchedulerPanel from "@/components/SyncSchedulerPanel";

function PresenceBadge({ present, secret }) {
  if (!present) {
    return <span className="text-[10px] font-mono text-gray-400">not set</span>;
  }
  return (
    <span className="text-[10px] font-mono text-green-700 flex items-center gap-1">
      <Check className="w-3 h-3" /> {secret || "configured"}
    </span>
  );
}

export default function IntegrationsSettings() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({
    hubspot_token: "",
    slack_webhook_url: "",
    sendgrid_api_key: "",
    sendgrid_from_email: "",
    sendgrid_from_name: "",
    notify_recipients: "",
    hubspot_oauth_client_id: "",
    hubspot_oauth_client_secret: "",
    sf_username: "",
    sf_password: "",
    sf_security_token: "",
    sf_client_id: "",
    sf_client_secret: "",
    sf_login_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/integrations/config");
    setState(data);
    setForm((f) => ({
      ...f,
      sendgrid_from_email: data.sendgrid_from_email || "",
      sendgrid_from_name: data.sendgrid_from_name || "",
      notify_recipients: (data.notify_recipients || []).join(", "),
      hubspot_oauth_client_id: data.hubspot_oauth_client_id || "",
      sf_username: data.sf_username || "",
      sf_login_url: data.sf_login_url || "https://login.salesforce.com",
      sf_client_id: data.sf_client_id || "",
    }));
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        // Only send non-empty secrets so a masked value from server doesn't overwrite
        hubspot_token: form.hubspot_token || undefined,
        slack_webhook_url: form.slack_webhook_url || undefined,
        sendgrid_api_key: form.sendgrid_api_key || undefined,
        sendgrid_from_email: form.sendgrid_from_email || undefined,
        sendgrid_from_name: form.sendgrid_from_name || undefined,
        notify_recipients: form.notify_recipients
          .split(",").map((s) => s.trim()).filter(Boolean),
        hubspot_oauth_client_id: form.hubspot_oauth_client_id || undefined,
        hubspot_oauth_client_secret: form.hubspot_oauth_client_secret || undefined,
        sf_username: form.sf_username || undefined,
        sf_password: form.sf_password || undefined,
        sf_security_token: form.sf_security_token || undefined,
        sf_client_id: form.sf_client_id || undefined,
        sf_client_secret: form.sf_client_secret || undefined,
        sf_login_url: form.sf_login_url || undefined,
      };
      await api.put("/integrations/config", payload);
      toast.success("Integrations saved");
      setForm((f) => ({
        ...f,
        hubspot_token: "", slack_webhook_url: "", sendgrid_api_key: "",
        hubspot_oauth_client_secret: "",
        sf_password: "", sf_security_token: "", sf_client_secret: "",
      }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearSecret = async (key) => {
    try {
      await api.put("/integrations/config", { [key]: "" });
      toast.success("Cleared");
      load();
    } catch {
      toast.error("Clear failed");
    }
  };

  const runTest = async (name, endpoint) => {
    setTesting((t) => ({ ...t, [name]: true }));
    try {
      const { data } = await api.post(endpoint);
      if (data.ok) {
        toast.success(`${name}: OK`);
      } else {
        toast.error(`${name}: ${data.error || "failed"}`);
      }
    } catch (err) {
      toast.error(`${name}: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTesting((t) => ({ ...t, [name]: false }));
    }
  };

  const syncHubspot = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/hubspot/sync");
      if (data.ok === false) {
        toast.error(`HubSpot sync: ${data.error}`);
      } else {
        toast.success(`Imported ${data.imported}, updated ${data.updated}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const syncHubspotOAuth = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/hubspot/oauth/sync");
      if (data.ok === false) {
        toast.error(`HubSpot sync: ${data.error}`);
      } else {
        toast.success(`Imported ${data.imported}, updated ${data.updated}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const connectHubspotOAuth = async () => {
    try {
      const { data } = await api.get("/integrations/hubspot/oauth/authorize-url");
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start OAuth flow");
    }
  };

  const disconnectHubspotOAuth = async () => {
    try {
      await api.post("/integrations/hubspot/oauth/disconnect");
      toast.success("HubSpot OAuth session disconnected");
      load();
    } catch {
      toast.error("Disconnect failed");
    }
  };

  const syncSalesforce = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/salesforce/sync");
      if (data.ok === false) {
        toast.error(`Salesforce sync: ${data.error}`);
      } else {
        toast.success(`Imported ${data.imported}, updated ${data.updated}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // Debounced wrappers — 3-second cooldown on every action button
  const dHubspotTest = useDebouncedAction(() => runTest("HubSpot", "/integrations/hubspot/test"));
  const dHubspotSync = useDebouncedAction(syncHubspot);
  const dHsOauthSync = useDebouncedAction(syncHubspotOAuth);
  const dSlackTest = useDebouncedAction(() => runTest("Slack", "/integrations/slack/test"));
  const dEmailTest = useDebouncedAction(() => runTest("Email", "/integrations/email/test"));
  const dSfTest = useDebouncedAction(() => runTest("Salesforce", "/integrations/salesforce/test"));
  const dSfSync = useDebouncedAction(syncSalesforce);

  if (!state) return <div className="text-sm text-gray-400 py-8">Loading integrations…</div>;

  return (
    <div className="space-y-6" data-testid="integrations-settings">
      <SyncSchedulerPanel />

      {/* HubSpot */}
      <div className="border border-gray-200 bg-white rounded-sm p-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-3">
            <Cloud className="w-5 h-5 mt-1 text-gray-500" strokeWidth={1.5} />
            <div>
              <div className="overline text-gray-400 mb-1">CRM Ingestion · Private App</div>
              <div className="font-heading text-lg font-medium">HubSpot</div>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-md">
                Paste a HubSpot Private App token with <code className="font-mono">crm.objects.deals.read</code> scope. iSale will pull your deals into the pipeline and score them via the FFT layer.
              </p>
            </div>
          </div>
          <PresenceBadge present={state.presence.hubspot_token} secret={state.raw?.hubspot_token} />
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="hs-token" className="overline text-gray-500 mb-2 block">Private App token</Label>
            <div className="flex items-center gap-2">
              <Input
                id="hs-token" type="password"
                placeholder={state.presence.hubspot_token ? "•••••• (leave blank to keep)" : "pat-na1-xxxxxxxx-..."}
                value={form.hubspot_token}
                onChange={(e) => setForm({ ...form, hubspot_token: e.target.value })}
                className="rounded-sm h-10 font-mono text-xs"
                data-testid="hubspot-token-input"
              />
              {state.presence.hubspot_token && (
                <Button
                  variant="ghost" size="sm"
                  onClick={() => clearSecret("hubspot_token")}
                  className="rounded-sm h-10 text-gray-400 hover:text-red-600"
                  data-testid="hubspot-clear"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button
            onClick={dHubspotTest.run}
            disabled={dHubspotTest.disabled || !state.presence.hubspot_token}
            variant="outline"
            className="rounded-sm gap-2"
            data-testid="hubspot-test-btn"
          >
            <Zap className={`w-3.5 h-3.5 ${testing.HubSpot ? "animate-pulse" : ""}`} />
            {dHubspotTest.cooldownRemaining > 0 ? `Wait ${dHubspotTest.cooldownRemaining}s` : "Test connection"}
          </Button>
          <Button
            onClick={dHubspotSync.run}
            disabled={dHubspotSync.disabled || !state.presence.hubspot_token}
            className="rounded-sm bg-black hover:bg-gray-800 gap-2"
            data-testid="hubspot-sync-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            {dHubspotSync.cooldownRemaining > 0 ? `Wait ${dHubspotSync.cooldownRemaining}s` : (syncing ? "Syncing…" : "Sync deals now")}
          </Button>
        </div>
      </div>

      {/* HubSpot OAuth2 (multi-tenant scaffold) */}
      <div className="border border-gray-200 bg-white rounded-sm p-8" data-testid="hubspot-oauth-card">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-3">
            <Cloud className="w-5 h-5 mt-1 text-gray-500" strokeWidth={1.5} />
            <div>
              <div className="overline text-gray-400 mb-1">CRM Ingestion · OAuth 2.0 (multi-tenant)</div>
              <div className="font-heading text-lg font-medium">HubSpot · Connected App</div>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-md">
                Register a HubSpot app, paste its <code className="font-mono">client_id</code> + <code className="font-mono">client_secret</code>, then click <strong>Connect</strong> to grant iSale access via the 3-legged OAuth flow. Tokens are stored per-tenant and auto-refreshed.
              </p>
            </div>
          </div>
          {state.presence.hubspot_oauth_connected ? (
            <span className="text-[10px] font-mono text-green-700 flex items-center gap-1">
              <Check className="w-3 h-3" /> connected
            </span>
          ) : state.presence.hubspot_oauth_configured ? (
            <span className="text-[10px] font-mono text-orange-600 flex items-center gap-1">
              ready · not connected
            </span>
          ) : (
            <span className="text-[10px] font-mono text-gray-400">not configured</span>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="overline text-gray-500 mb-2 block">Client ID</Label>
            <Input
              placeholder="1234-abcd-..."
              value={form.hubspot_oauth_client_id}
              onChange={(e) => setForm({ ...form, hubspot_oauth_client_id: e.target.value })}
              className="rounded-sm h-10 font-mono text-xs"
              data-testid="hs-oauth-cid-input"
            />
          </div>
          <div>
            <Label className="overline text-gray-500 mb-2 block">Client Secret</Label>
            <Input
              type="password"
              placeholder={state.presence.hubspot_oauth_configured ? "•••••• (leave blank to keep)" : "secret"}
              value={form.hubspot_oauth_client_secret}
              onChange={(e) => setForm({ ...form, hubspot_oauth_client_secret: e.target.value })}
              className="rounded-sm h-10 font-mono text-xs"
              data-testid="hs-oauth-csecret-input"
            />
          </div>
        </div>

        <div className="mt-4 text-[11px] font-mono text-gray-500 bg-gray-50 border border-gray-200 rounded-sm p-3">
          Redirect URI to whitelist in your HubSpot app settings:
          <div className="mt-1 text-gray-900 break-all">
            {(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/integrations/hubspot/oauth/callback
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          {state.presence.hubspot_oauth_connected ? (
            <>
              <Button
                onClick={disconnectHubspotOAuth}
                variant="outline"
                className="rounded-sm gap-2"
                data-testid="hs-oauth-disconnect-btn"
              >
                <X className="w-3.5 h-3.5" /> Disconnect
              </Button>
              <Button
                onClick={dHsOauthSync.run}
                disabled={dHsOauthSync.disabled}
                className="rounded-sm bg-black hover:bg-gray-800 gap-2"
                data-testid="hs-oauth-sync-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
                {dHsOauthSync.cooldownRemaining > 0 ? `Wait ${dHsOauthSync.cooldownRemaining}s` : (syncing ? "Syncing…" : "Sync deals (OAuth)")}
              </Button>
            </>
          ) : (
            <Button
              onClick={connectHubspotOAuth}
              disabled={!state.presence.hubspot_oauth_configured}
              className="rounded-sm bg-black hover:bg-gray-800 gap-2"
              data-testid="hs-oauth-connect-btn"
            >
              <Cloud className="w-3.5 h-3.5" />
              Connect to HubSpot
            </Button>
          )}
        </div>
      </div>

      {/* Salesforce */}
      <div className="border border-gray-200 bg-white rounded-sm p-8" data-testid="salesforce-card">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-3">
            <Cloud className="w-5 h-5 mt-1 text-gray-500" strokeWidth={1.5} />
            <div>
              <div className="overline text-gray-400 mb-1">CRM Ingestion · OAuth 2.0 (username-password)</div>
              <div className="font-heading text-lg font-medium">Salesforce · Opportunities</div>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-md">
                Requires a Connected App with OAuth enabled. Pulls Opportunities via SOQL, follows <code className="font-mono">nextRecordsUrl</code> until done, maps to iSale deal schema.
              </p>
            </div>
          </div>
          {state.presence.salesforce_configured ? (
            <span className="text-[10px] font-mono text-green-700 flex items-center gap-1">
              <Check className="w-3 h-3" /> configured
            </span>
          ) : (
            <span className="text-[10px] font-mono text-gray-400">not set</span>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="overline text-gray-500 mb-2 block">Username</Label>
            <Input
              placeholder="you@company.com"
              value={form.sf_username}
              onChange={(e) => setForm({ ...form, sf_username: e.target.value })}
              className="rounded-sm h-10 text-xs"
              data-testid="sf-username-input"
            />
          </div>
          <div>
            <Label className="overline text-gray-500 mb-2 block">Login URL</Label>
            <Input
              placeholder="https://login.salesforce.com"
              value={form.sf_login_url}
              onChange={(e) => setForm({ ...form, sf_login_url: e.target.value })}
              className="rounded-sm h-10 font-mono text-xs"
              data-testid="sf-login-url-input"
            />
          </div>
          <div>
            <Label className="overline text-gray-500 mb-2 block">Password</Label>
            <Input
              type="password"
              placeholder={state.presence.salesforce_configured ? "•••••• (leave blank to keep)" : ""}
              value={form.sf_password}
              onChange={(e) => setForm({ ...form, sf_password: e.target.value })}
              className="rounded-sm h-10 font-mono text-xs"
              data-testid="sf-password-input"
            />
          </div>
          <div>
            <Label className="overline text-gray-500 mb-2 block">Security token</Label>
            <Input
              type="password"
              placeholder={state.presence.salesforce_configured ? "•••••• (leave blank to keep)" : ""}
              value={form.sf_security_token}
              onChange={(e) => setForm({ ...form, sf_security_token: e.target.value })}
              className="rounded-sm h-10 font-mono text-xs"
              data-testid="sf-token-input"
            />
          </div>
          <div>
            <Label className="overline text-gray-500 mb-2 block">Consumer key (client_id)</Label>
            <Input
              placeholder="3MVG9..."
              value={form.sf_client_id}
              onChange={(e) => setForm({ ...form, sf_client_id: e.target.value })}
              className="rounded-sm h-10 font-mono text-xs"
              data-testid="sf-cid-input"
            />
          </div>
          <div>
            <Label className="overline text-gray-500 mb-2 block">Consumer secret</Label>
            <Input
              type="password"
              placeholder={state.presence.salesforce_configured ? "•••••• (leave blank to keep)" : ""}
              value={form.sf_client_secret}
              onChange={(e) => setForm({ ...form, sf_client_secret: e.target.value })}
              className="rounded-sm h-10 font-mono text-xs"
              data-testid="sf-csecret-input"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button
            onClick={dSfTest.run}
            disabled={dSfTest.disabled || !state.presence.salesforce_configured}
            variant="outline"
            className="rounded-sm gap-2"
            data-testid="sf-test-btn"
          >
            <Zap className={`w-3.5 h-3.5 ${testing.Salesforce ? "animate-pulse" : ""}`} />
            {dSfTest.cooldownRemaining > 0 ? `Wait ${dSfTest.cooldownRemaining}s` : "Test connection"}
          </Button>
          <Button
            onClick={dSfSync.run}
            disabled={dSfSync.disabled || !state.presence.salesforce_configured}
            className="rounded-sm bg-black hover:bg-gray-800 gap-2"
            data-testid="sf-sync-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            {dSfSync.cooldownRemaining > 0 ? `Wait ${dSfSync.cooldownRemaining}s` : (syncing ? "Syncing…" : "Sync opportunities")}
          </Button>
        </div>
      </div>

      {/* Slack */}
      <div className="border border-gray-200 bg-white rounded-sm p-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-5 h-5 mt-1 text-gray-500" strokeWidth={1.5} />
            <div>
              <div className="overline text-gray-400 mb-1">Notifier · Incoming Webhook</div>
              <div className="font-heading text-lg font-medium">Slack</div>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-md">
                Fires on <strong>sustained KPI violations</strong> and their resolution. Paste an Incoming Webhook URL from your Slack workspace.
              </p>
            </div>
          </div>
          <PresenceBadge present={state.presence.slack_webhook_url} secret={state.raw?.slack_webhook_url} />
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="password"
            placeholder={state.presence.slack_webhook_url ? "•••••• (leave blank to keep)" : "https://hooks.slack.com/services/T.../B.../..."}
            value={form.slack_webhook_url}
            onChange={(e) => setForm({ ...form, slack_webhook_url: e.target.value })}
            className="rounded-sm h-10 font-mono text-xs"
            data-testid="slack-url-input"
          />
          {state.presence.slack_webhook_url && (
            <Button variant="ghost" size="sm" onClick={() => clearSecret("slack_webhook_url")} className="rounded-sm h-10 text-gray-400 hover:text-red-600" data-testid="slack-clear">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={dSlackTest.run}
            disabled={dSlackTest.disabled || !state.presence.slack_webhook_url}
            variant="outline"
            className="rounded-sm gap-2"
            data-testid="slack-test-btn"
          >
            <Zap className={`w-3.5 h-3.5 ${testing.Slack ? "animate-pulse" : ""}`} />
            {dSlackTest.cooldownRemaining > 0 ? `Wait ${dSlackTest.cooldownRemaining}s` : "Send test message"}
          </Button>
        </div>
      </div>

      {/* SendGrid */}
      <div className="border border-gray-200 bg-white rounded-sm p-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 mt-1 text-gray-500" strokeWidth={1.5} />
            <div>
              <div className="overline text-gray-400 mb-1">Notifier · SendGrid v3</div>
              <div className="font-heading text-lg font-medium">Email</div>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-md">
                HTML email alerts on sustained/resolved violations. Requires a SendGrid API key and a verified sender.
              </p>
            </div>
          </div>
          <PresenceBadge present={state.presence.sendgrid_api_key} secret={state.raw?.sendgrid_api_key} />
        </div>

        <div className="space-y-4">
          <div>
            <Label className="overline text-gray-500 mb-2 block">API key</Label>
            <div className="flex items-center gap-2">
              <Input
                type="password"
                placeholder={state.presence.sendgrid_api_key ? "•••••• (leave blank to keep)" : "SG.xxxxxxxx..."}
                value={form.sendgrid_api_key}
                onChange={(e) => setForm({ ...form, sendgrid_api_key: e.target.value })}
                className="rounded-sm h-10 font-mono text-xs"
                data-testid="sendgrid-key-input"
              />
              {state.presence.sendgrid_api_key && (
                <Button variant="ghost" size="sm" onClick={() => clearSecret("sendgrid_api_key")} className="rounded-sm h-10 text-gray-400 hover:text-red-600" data-testid="sendgrid-clear">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="overline text-gray-500 mb-2 block">From email</Label>
              <Input
                type="email"
                placeholder="alerts@yourdomain.com"
                value={form.sendgrid_from_email}
                onChange={(e) => setForm({ ...form, sendgrid_from_email: e.target.value })}
                className="rounded-sm h-10"
                data-testid="sendgrid-from-input"
              />
            </div>
            <div>
              <Label className="overline text-gray-500 mb-2 block">From name (optional)</Label>
              <Input
                placeholder="iSale KPI Monitor"
                value={form.sendgrid_from_name}
                onChange={(e) => setForm({ ...form, sendgrid_from_name: e.target.value })}
                className="rounded-sm h-10"
                data-testid="sendgrid-name-input"
              />
            </div>
          </div>
          <div>
            <Label className="overline text-gray-500 mb-2 block">Recipients (comma-separated)</Label>
            <Input
              placeholder="ops@you.com, cto@you.com"
              value={form.notify_recipients}
              onChange={(e) => setForm({ ...form, notify_recipients: e.target.value })}
              className="rounded-sm h-10 font-mono text-xs"
              data-testid="notify-recipients-input"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={dEmailTest.run}
            disabled={dEmailTest.disabled || !state.presence.sendgrid_api_key}
            variant="outline"
            className="rounded-sm gap-2"
            data-testid="email-test-btn"
          >
            <Zap className={`w-3.5 h-3.5 ${testing.Email ? "animate-pulse" : ""}`} />
            {dEmailTest.cooldownRemaining > 0 ? `Wait ${dEmailTest.cooldownRemaining}s` : "Send test email"}
          </Button>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-sm bg-black hover:bg-gray-800 gap-2"
          data-testid="save-integrations-btn"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? "Saving…" : "Save all changes"}
        </Button>
      </div>
    </div>
  );
}
